import {
  buildManageAccountUrl,
  STRONG_AUTH_REMINDER_INTENT,
} from "@arcadeai/ui-kit/utils/auth-redirect";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { coordinatorRootApi } from "./api";

const DAY_MS = 24 * 60 * 60 * 1000;
const STORAGE_KEY_PREFIX = "arcade.strong-auth-reminder.";

type ReminderStorage = Pick<Storage, "getItem" | "setItem">;

/**
 * This browser's localStorage, reached only when read or written, so a browser
 * that refuses storage access fails inside the helpers rather than at render.
 */
export const browserStorage: ReminderStorage = {
  getItem: (key) => window.localStorage.getItem(key),
  setItem: (key, value) => window.localStorage.setItem(key, value),
};

/**
 * Whether to remind this operator to set up strong authentication now: they
 * haven't put it off in the last day. Storage that can't be read means
 * reminding every page load rather than never.
 */
export function isReminderDue({
  accountId,
  now,
  storage,
}: {
  accountId: string;
  now: number;
  storage: ReminderStorage;
}): boolean {
  let putOffAt: number;
  try {
    putOffAt = Number(storage.getItem(STORAGE_KEY_PREFIX + accountId));
  } catch {
    return true;
  }
  return !(putOffAt > 0 && now - putOffAt < DAY_MS);
}

/** Puts the reminder off for a day, for this operator in this browser. */
export function remindLater({
  accountId,
  now,
  storage,
}: {
  accountId: string;
  now: number;
  storage: ReminderStorage;
}): void {
  try {
    storage.setItem(STORAGE_KEY_PREFIX + accountId, String(now));
  } catch {
    // Without storage, the reminder comes back on the next page load.
  }
}

/** Manage my account, opened to set up strong authentication before `deadline`. */
export function strongAuthReminderUrl(
  accountUiUrl: string,
  { returnTo, deadline }: { returnTo: string; deadline: string }
): string {
  return buildManageAccountUrl(accountUiUrl, {
    intent: STRONG_AUTH_REMINDER_INTENT,
    deadline,
    returnTo,
  });
}

const whoAmISchema = z.object({
  data: z.object({
    account_id: z.string(),
    full_name: z.string().optional(),
    preferred_name: z.string().nullish(),
    // Absent when the requirement doesn't cover the account and there's no
    // grace period, or the sign-in doesn't say how it authenticated.
    mfa: z
      .object({
        status: z.string(),
        // The first day strong authentication is required (YYYY-MM-DD); set
        // only during the grace period.
        deadline: z.iso.date().nullish(),
      })
      .nullish(),
  }),
});

/** Who's signed in, and where their sign-in stands against the requirement. */
export const whoAmIQueryOptions = queryOptions({
  queryKey: ["coordinator", "whoami"],
  staleTime: Number.POSITIVE_INFINITY,
  queryFn: async () => {
    const { data } = await coordinatorRootApi.get<unknown>("/auth/whoami");
    return whoAmISchema.parse(data).data;
  },
});
